import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ArrowRight, Scan, Users, EnvelopeSimple, IdentificationBadge, Sparkle, ListChecks, ArrowUpRight } from "@phosphor-icons/react";
import ROICalculator from "../components/ROICalculator";

const features = [
  { icon: Scan, title: "Scan in seconds", body: "Snap a business card. AI extracts every field — name, role, company, contact — straight into your CRM." },
  { icon: IdentificationBadge, title: "Share your card", body: "Your own digital business card with QR code. They scan, save, and you have their info in return." },
  { icon: ListChecks, title: "Never drop a lead", body: "Follow-up reminders for coffee dates, intros, and virtual meetings. Stage-based pipeline tracking." },
  { icon: EnvelopeSimple, title: "Message templates", body: "Pre-built and AI-generated outreach for every relationship stage — intro, warm-up, coffee invite, check-in." },
  { icon: Sparkle, title: "Auto campaigns", body: "Trigger sequences when contacts move from 'New' to 'Engaged'. Stay top-of-mind without lifting a finger." },
  { icon: Users, title: "Relationship pipeline", body: "Visualize every contact's stage — from cold intro to active deal partner." },
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen relative" style={{ background: "var(--bg)" }}>
      {/* Nav */}
      <header className="border-b sticky top-0 z-30" style={{ borderColor: "var(--border)", background: "rgba(10,10,10,0.85)", backdropFilter: "blur(12px)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="landing-logo" className="font-display font-black text-2xl tracking-tighter">
            MY NEXUS<span style={{ color: "var(--accent)" }}> </span>CARD
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm font-medium hover:text-white" style={{ color: "var(--text-2)" }}>Features</a>
            <a href="#how" className="text-sm font-medium hover:text-white" style={{ color: "var(--text-2)" }}>How it works</a>
            <a href="#why" className="text-sm font-medium hover:text-white" style={{ color: "var(--text-2)" }}>Why it works</a>
            <a href="#pipeline" className="text-sm font-medium hover:text-white" style={{ color: "var(--text-2)" }}>Pipeline</a>
            <a href="#pricing" className="text-sm font-medium hover:text-white" style={{ color: "var(--text-2)" }}>Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <button onClick={() => nav("/dashboard")} data-testid="cta-dashboard" className="btn-primary">Dashboard</button>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-[#C7DDE5] hover:text-white">Sign in</Link>
                <Link to="/register" data-testid="nav-register" className="btn-primary text-sm">Get started</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden hero-noise grid-bg">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 fade-up">
            <div className="overline mb-6" data-testid="hero-overline">// for entrepreneurs &amp; sales operators</div>
            <h1 className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
              Every handshake.<br />
              Every coffee.<br />
              <span style={{ color: "var(--accent)" }}>Converted.</span>
            </h1>
            <p className="mt-8 text-lg text-[#C7DDE5] max-w-xl leading-relaxed">
              Scan business cards into a CRM that thinks like an operator. Track every relationship from cold intro to active deal. Send your digital card with a tap. Never forget a follow-up.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button onClick={() => nav(user ? "/dashboard" : "/register")} data-testid="hero-cta-primary" className="btn-primary flex items-center gap-3">
                Start building your network <ArrowRight weight="bold" />
              </button>
              <a href="#features" data-testid="hero-cta-secondary" className="btn-secondary">See how it works</a>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <div>
                <div className="font-display text-3xl font-bold">2 sec</div>
                <div className="overline mt-2">Card → CRM</div>
              </div>
              <div>
                <div className="font-display text-3xl font-bold">5</div>
                <div className="overline mt-2">Pipeline stages</div>
              </div>
              <div>
                <div className="font-display text-3xl font-bold">0</div>
                <div className="overline mt-2">Dropped leads</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up delay-2">
            <div className="border" style={{ borderColor: "var(--border)" }}>
              <img src="https://images.unsplash.com/photo-1600147131759-880e94a6185f?crop=entropy&cs=srgb&fm=jpg&w=940&q=85"
                alt="Business card scan" className="w-full h-[480px] object-cover" />
            </div>
            <div className="grid grid-cols-2 gap-0 mt-0">
              <div className="border border-t-0 p-4" style={{ borderColor: "var(--border)" }}>
                <div className="overline">live status</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="stage-dot pulse-red" style={{ background: "var(--accent)" }}></span>
                  <span className="font-mono text-sm">Scanning enabled</span>
                </div>
              </div>
              <div className="border border-t-0 border-l-0 p-4" style={{ borderColor: "var(--border)" }}>
                <div className="overline">last sync</div>
                <div className="mt-2 font-mono text-sm">just now</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-7">
              <div className="overline mb-4">// modules</div>
              <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-none">
                Six tools.<br />One system of record.
              </h2>
            </div>
            <p className="md:col-span-5 text-[#C7DDE5] leading-relaxed">
              Built for the way real entrepreneurs work the room: capture fast, follow up faster, and never let a high-signal contact go cold.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
            {features.map((f, i) => (
              <div key={i} className="border-r border-b p-8 transition-colors hover:bg-[#0F1E24]" style={{ borderColor: "var(--border)" }} data-testid={`feature-${i}`}>
                <f.icon size={28} weight="duotone" color="#2DD4D4" />
                <h3 className="font-display font-bold text-xl mt-6">{f.title}</h3>
                <p className="text-sm text-[#C7DDE5] mt-3 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pipeline */}
      <section id="pipeline" className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="overline mb-4">// pipeline</div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mb-10">From handshake to deal partner.</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
            {[
              { c: "#007AFF", l: "New", d: "Just met. Fresh." },
              { c: "#FF9500", l: "Warming", d: "First reply sent." },
              { c: "#BF5AF2", l: "Engaged", d: "Talking regularly." },
              { c: "#34C759", l: "Active", d: "Doing work together." },
              { c: "#7BA0AE", l: "Dormant", d: "Time to revive." },
            ].map((s, i) => (
              <div key={i} className="border-r border-b p-6" style={{ borderColor: "var(--border)" }}>
                <span className="stage-dot" style={{ background: s.c }}></span>
                <div className="font-display font-bold text-2xl mt-3">{s.l}</div>
                <div className="overline mt-1">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to execute */}
      <section id="how" className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="overline mb-4">// how to execute</div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mb-12">Four steps. Every event.</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
            {[
              { n: "01", t: "Scan or share", b: "At the event, snap their card or hand them your QR. Two-second capture." },
              { n: "02", t: "Set the stage", b: "Tag them New → Warming → Engaged. Stage drives everything that follows." },
              { n: "03", t: "Send the follow-up", b: "Pick a template or generate one with AI. Resend delivers within seconds." },
              { n: "04", t: "Stay top-of-mind", b: "Auto-campaigns ping you when it's time to message — coffee, intro, check-in." },
            ].map((s) => (
              <div key={s.n} className="border-r border-b p-6" style={{ borderColor: "var(--border)" }}>
                <div className="font-mono text-sm" style={{ color: "var(--accent)" }}>{s.n}</div>
                <h3 className="font-display font-bold text-xl mt-4">{s.t}</h3>
                <p className="text-sm mt-3 leading-relaxed" style={{ color: "var(--text-2)" }}>{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real-life scenarios */}
      <section className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="overline mb-4">// real scenarios</div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mb-12">Built for the way you sell.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
            {[
              { tag: "Trade show", t: "You collect 40 cards in 6 hours", b: "Scan each on the floor. By the time you're back at the hotel, every contact is in stage 'New' with a draft intro email queued. Send them all in 12 minutes." },
              { tag: "Coffee chat", t: "You meet a potential investor", b: "Move them to 'Engaged' from your phone. Auto-campaign schedules a follow-up email in 2 days, an LinkedIn nudge in 7, and a 'still interested?' check-in at day 21." },
              { tag: "Conference booth", t: "Strangers ask for your info", b: "Tap your QR — they save your card, optionally send theirs back, and (referral mode on) introduce one person who'd love to know you." },
            ].map((s, i) => (
              <div key={i} className="border-r border-b p-8" style={{ borderColor: "var(--border)" }}>
                <span className="tag" style={{ color: "var(--accent)", borderColor: "var(--accent)" }}>{s.tag}</span>
                <h3 className="font-display font-bold text-2xl mt-5">{s.t}</h3>
                <p className="text-base mt-4 leading-relaxed" style={{ color: "var(--text-2)" }}>{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why this works — data-backed section */}
      <section id="why" className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-8">
              <div className="overline mb-4">// why it works</div>
              <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-none">
                Referrals aren't luck.<br />They're a <span style={{ color: "var(--accent)" }}>system</span>.
              </h2>
            </div>
            <p className="md:col-span-4 text-[#C7DDE5] leading-relaxed">
              The numbers below aren't ours — they're industry-verified. My Nexus Card is engineered to convert every one of them into your pipeline.
            </p>
          </div>

          {/* Big stat grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
            {[
              { stat: "84%", label: "of B2B buyers start with a referral", note: "Referrals are the #1 entry point in B2B purchasing.", src: "Edelman / LinkedIn B2B Institute, 2021" },
              { stat: "51%", label: "of sales reps say referrals are their most effective lead source", note: "Beats cold outbound, ads, and content marketing.", src: "HubSpot State of Sales, 2024" },
              { stat: "88%", label: "of consumers trust recommendations from people they know", note: "Trust is the pre-sale — you inherit it.", src: "Nielsen Global Trust in Advertising, 2021" },
              { stat: "37%", label: "higher retention rate from referred customers", note: "They stay longer, buy more, refer others.", src: "Bond Brand Loyalty Report" },
            ].map((s, i) => (
              <div key={i} className="border-r border-b p-8" style={{ borderColor: "var(--border)" }} data-testid={`stat-${i}`}>
                <div className="font-display font-black text-6xl tracking-tighter" style={{ color: "var(--accent)" }}>{s.stat}</div>
                <div className="mt-4 font-semibold text-lg leading-snug">{s.label}</div>
                <p className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>{s.note}</p>
                <div className="overline mt-4" style={{ color: "var(--text-3)" }}>src: {s.src}</div>
              </div>
            ))}
          </div>

          {/* The leak → how we plug it */}
          <div className="mt-16">
            <div className="overline mb-6">// where money leaks in your network</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
              {[
                {
                  problem: "You collect cards. You don't follow up.",
                  data: "60% of buyers say no four times before saying yes; 48% of sellers never follow up even once.",
                  loss: "Most of your event ROI evaporates in 72 hours",
                  fix: "AI card scanner + auto-tag by event → every card in your CRM within 2 seconds. Follow-up drafts pre-queued before you leave the venue.",
                  src: "Invesp Sales Follow-up Statistics, 2023",
                },
                {
                  problem: "50% of what was discussed is forgotten within 24 hours",
                  data: "The Ebbinghaus forgetting curve — replicated in every modern memory study.",
                  loss: "Your follow-up sounds generic. They stop reading.",
                  fix: "Every contact tagged with the event and the topic. Templates reference {{event}} and your personal notes so outreach reads like it was written by someone who remembers.",
                  src: "Ebbinghaus (1885) · Murre & Dros replication (2015)",
                },
                {
                  problem: "91% of clients would give a referral. Only 11% get asked.",
                  data: "80-point gap between willingness and action.",
                  loss: "Your warmest pipeline source is completely dormant.",
                  fix: "Referral mode on your digital card: every person who saves your info is prompted to refer one person who'd love to know you. Automated, embarrassment-free, always-on.",
                  src: "Julie Littlechild / Advisor Impact Client Insight Survey",
                },
              ].map((p, i) => (
                <div key={i} className="border-r border-b p-8" style={{ borderColor: "var(--border)" }} data-testid={`leak-${i}`}>
                  <div className="overline" style={{ color: "var(--accent)" }}>// leak {String(i + 1).padStart(2, "0")}</div>
                  <h3 className="font-display font-bold text-2xl mt-4 leading-tight">{p.problem}</h3>
                  <div className="mt-3 font-mono text-sm" style={{ color: "var(--text-2)" }}>{p.data}</div>
                  <div className="mt-2 text-sm font-semibold" style={{ color: "var(--accent)" }}>→ {p.loss}</div>
                  <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--border)" }}>
                    <div className="overline mb-2">// how we plug it</div>
                    <p className="text-sm leading-relaxed">{p.fix}</p>
                    <div className="overline mt-4" style={{ color: "var(--text-3)" }}>src: {p.src}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Interactive ROI calculator */}
          <div className="mt-16">
            <ROICalculator />
          </div>

          {/* Who this is for */}
          <div className="mt-16">
            <div className="overline mb-6">// who wins with this</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
              {[
                { role: "Financial advisors", why: "Referrals are 79% of your book. Never miss a warm intro." },
                { role: "Real estate agents", why: "Every open house is 30+ cards. Auto follow-ups keep listings top-of-mind." },
                { role: "Insurance brokers", why: "One networking mixer = a year of retention if you follow through." },
                { role: "Founders & BD reps", why: "Trade shows pay off only when the pipeline gets worked. Automate the work." },
              ].map((r, i) => (
                <div key={i} className="border-r border-b p-6" style={{ borderColor: "var(--border)" }} data-testid={`who-${i}`}>
                  <div className="font-display font-bold text-lg">{r.role}</div>
                  <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>{r.why}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-10 text-xs text-center" style={{ color: "var(--text-3)" }}>
            Stats sourced from Edelman/LinkedIn B2B Institute, HubSpot State of Sales, Nielsen Global Trust, Bond Brand Loyalty, Invesp, Ebbinghaus/Murre &amp; Dros, and Julie Littlechild/Advisor Impact. Individual results vary based on effort, offer, and audience quality.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="overline mb-4">// operators talking</div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mb-12">What the room is saying.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
            {[
              { q: "Closed a 6-figure deal that started as a stranger at a chamber mixer. The auto follow-up was already drafted by the time I got home.", n: "Marcus T.", r: "Insurance broker" },
              { q: "I used to lose business cards in my jacket pocket. Now I scan, tag, and the app reminds me to circle back. My referral count tripled in 90 days.", n: "Priya S.", r: "Real estate agent" },
              { q: "The QR card with referral mode is genius. Every event I run, two or three people refer someone before I've even followed up.", n: "Jordan L.", r: "Founder, B2B SaaS" },
            ].map((t, i) => (
              <div key={i} className="border-r border-b p-8" style={{ borderColor: "var(--border)" }}>
                <p className="text-base leading-relaxed">"{t.q}"</p>
                <div className="mt-6 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
                  <div className="font-semibold">{t.n}</div>
                  <div className="overline mt-1">{t.r}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="overline mb-4">// pricing</div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mb-4">Priced for operators.</h2>
          <p className="mb-12 max-w-xl" style={{ color: "var(--text-2)" }}>Start free. Upgrade when your roster gets serious. No seats, no per-contact fees.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
            {[
              { name: "Free", price: "$0", per: "/forever", feats: ["Up to 50 contacts", "10 scans/mo", "100 emails/mo", "Digital card + QR"], cta: "Start free", primary: false, action: () => nav(user ? "/dashboard" : "/register") },
              { name: "Starter", price: "Custom", per: "monthly + setup", feats: ["500 contacts", "50 scans/mo", "1,000 emails/mo", "Events tagging", "Referral mode"], cta: "Book a call", primary: false, action: () => nav("/book-a-call?plan=Starter") },
              { name: "Pro", price: "Custom", per: "monthly + setup", feats: ["Unlimited contacts", "25k emails/mo", "AI drafts + auto campaigns", "Email + SMS sending"], cta: "Book a call", primary: true, action: () => nav("/book-a-call?plan=Pro") },
              { name: "Team", price: "Custom", per: "per-user + setup", feats: ["Everything in Pro", "Unlimited emails", "Shared pipeline", "Priority support"], cta: "Book a call", primary: false, action: () => nav("/book-a-call?plan=Team") },
            ].map((p) => (
              <div key={p.name} className="border-r border-b p-8 flex flex-col" style={{ borderColor: p.primary ? "var(--accent)" : "var(--border)" }}>
                <div className="overline" style={p.primary ? { color: "var(--accent)" } : {}}>{p.name}</div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display font-black text-5xl tracking-tighter">{p.price}</span>
                  <span className="font-mono text-sm" style={{ color: "var(--text-2)" }}>{p.per}</span>
                </div>
                <ul className="mt-6 space-y-2 flex-1">
                  {p.feats.map((f) => (
                    <li key={f} className="text-sm flex items-start gap-2"><span style={{ color: "var(--accent)" }}>→</span> {f}</li>
                  ))}
                </ul>
                <button onClick={p.action} data-testid={`pricing-${p.name.toLowerCase()}`}
                  className={p.primary ? "btn-primary mt-6 w-full" : "btn-secondary mt-6 w-full"}>{p.cta}</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline mb-4">// ready</div>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-none">
              Your next deal is in your <span style={{ color: "var(--accent)" }}>pocket</span>. Stop losing it.
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col gap-4">
            <button onClick={() => nav(user ? "/dashboard" : "/register")} data-testid="cta-final" className="btn-primary flex items-center justify-between text-lg">
              Get started free <ArrowUpRight weight="bold" />
            </button>
            <p className="text-sm text-[#C7DDE5]">No credit card. Built for solopreneurs, BD reps, and founders.</p>
          </div>
        </div>
      </section>

      <footer className="border-t py-8" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-4">
          <div className="overline">© 2026 My Nexus Card · networking system for operators · BETA</div>
          <div className="flex items-center gap-4">
            <Link to="/terms" data-testid="footer-terms" className="overline hover:text-white">Terms</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="overline hover:text-white">Privacy</Link>
            <a href="mailto:georgeatabora@gmail.com" className="overline hover:text-white">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
